import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Book, Heart, BookmarkSimple, ChatCircle, Eye, Clock, ArrowLeft, ShareNetwork } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useReadingProgress } from '../hooks/useReadingProgress';
import AddToCollectionButton from '../components/AddToCollectionButton';
import GlobalHeader from '../components/GlobalHeader';
import { createSafeRichHTML } from '../lib/sanitize';

export const ArticleDetail = () => {
  const { articleId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [article, setArticle] = useState(null);
  const [author, setAuthor] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [liked, setLiked] = useState(false);
  const [bookmarked, setBookmarked] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Track scroll-progress for Continue Reading
  useReadingProgress(articleId, user);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const articleRes = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/articles/${articleId}`);
        setArticle(articleRes.data);

        // Fetch author details
        const authorRes = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/users/${articleRes.data.author_id}`);
        setAuthor(authorRes.data);

        // Fetch comments
        const commentsRes = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/articles/${articleId}/comments`);
        setComments(commentsRes.data);

        // Fetch interaction state if logged in
        if (user) {
          try {
            const stateRes = await axios.get(
              `${process.env.REACT_APP_BACKEND_URL}/api/articles/${articleId}/interaction-state`,
              { withCredentials: true }
            );
            setLiked(stateRes.data.liked);
            setBookmarked(stateRes.data.bookmarked);
          } catch (e) {
            console.error('Failed to fetch article state:', e);
          }
        }
      } catch (error) {
        console.error('Error loading article:', error);
        toast.error('Failed to load article');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [articleId, user]);

  const handleLike = async () => {
    if (!user) {
      navigate('/login');
      return;
    }
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/articles/${articleId}/like`,
        {},
        { withCredentials: true }
      );
      setLiked(res.data.liked);
      setArticle({ ...article, likes: article.likes + (res.data.liked ? 1 : -1) });
    } catch (error) {
      toast.error('Failed to like');
    }
  };

  const handleBookmark = async () => {
    if (!user) {
      navigate('/login');
      return;
    }
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/articles/${articleId}/bookmark`,
        {},
        { withCredentials: true }
      );
      setBookmarked(res.data.bookmarked);
      toast.success(res.data.bookmarked ? 'Bookmarked' : 'Removed from bookmarks');
    } catch (error) {
      toast.error('Failed to bookmark');
    }
  };

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success('Link copied to clipboard');
  };

  const handleComment = async () => {
    if (!user) {
      navigate('/login');
      return;
    }
    if (!commentText.trim()) return;

    try {
      setSubmitting(true);
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/articles/${articleId}/comments`,
        { content: commentText },
        { withCredentials: true }
      );
      setComments([res.data, ...comments]);
      setCommentText('');
      toast.success('Comment posted');
    } catch (error) {
      toast.error('Failed to post comment');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <GlobalHeader />
        <div className="flex items-center justify-center py-32">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-muted-foreground mb-4">Article not found</p>
          <Link to="/" className="text-primary hover:underline">Go home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <GlobalHeader />

      <article className="container mx-auto px-6 py-12">
        <div className="max-w-3xl mx-auto">
          <Link to="/browse" data-testid="back-button" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8 transition-colors">
            <ArrowLeft size={16} />
            Back to Browse
          </Link>

          {/* Category */}
          <div className="mb-6">
            <span className="text-xs font-semibold uppercase tracking-widest text-primary" data-testid="article-category">
              {article.category}
            </span>
          </div>

          {/* Title */}
          <h1 className="text-4xl md:text-6xl tracking-tight leading-tight mb-6" 
              style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 700 }}
              data-testid="article-title">
            {article.title}
          </h1>

          {/* Author info */}
          <div className="flex items-center gap-4 mb-8 pb-8 border-b border-border">
            <Link to={`/profile/${article.author_id}`} className="flex items-center gap-3" data-testid="author-link">
              <img 
                src={article.author_picture || `https://ui-avatars.com/api/?name=${article.author_name}`}
                alt={article.author_name}
                className="w-12 h-12 rounded-full border-2 border-border"
              />
              <div>
                <p className="font-semibold" data-testid="author-name">{article.author_name}</p>
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  <span>{new Date(article.published_at || article.created_at).toLocaleDateString()}</span>
                  <span className="flex items-center gap-1">
                    <Clock size={12} />
                    {article.reading_time} min read
                  </span>
                  <span className="flex items-center gap-1">
                    <Eye size={12} />
                    {article.views} views
                  </span>
                </div>
              </div>
            </Link>
          </div>

          {/* Featured Image */}
          {article.featured_image && (
            <div className="mb-8 rounded-lg overflow-hidden">
              <img src={article.featured_image} alt={article.title} className="w-full" />
            </div>
          )}

          {/* Content */}
          <div className="article-content editorial-drop-cap mb-12" data-testid="article-content"
               style={{ fontFamily: 'IBM Plex Sans, Mukta, sans-serif', fontSize: '1.125rem', lineHeight: '1.9' }}
               dangerouslySetInnerHTML={createSafeRichHTML(article.content)}>
          </div>

          {/* Tags */}
          {article.tags && article.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-12">
              {article.tags.map((tag, idx) => (
                <Link key={idx} to={`/browse?search=${tag}`} className="text-xs bg-muted px-3 py-1 rounded-full hover:bg-primary hover:text-primary-foreground transition-colors" data-testid={`tag-${idx}`}>
                  #{tag}
                </Link>
              ))}
            </div>
          )}

          {/* Actions bar */}
          <div className="flex items-center gap-4 py-6 border-y border-border mb-12 sticky bottom-0 bg-background z-40">
            <button 
              onClick={handleLike} 
              data-testid="like-button"
              className={`flex items-center gap-2 px-6 py-2.5 rounded-full border font-semibold transition-colors ${liked ? 'bg-red-50 border-red-200 text-red-600' : 'border-border hover:border-primary'}`}
            >
              <Heart size={20} weight={liked ? 'fill' : 'regular'} />
              {article.likes}
            </button>
            <button 
              onClick={handleBookmark}
              data-testid="bookmark-button" 
              className={`flex items-center gap-2 px-6 py-2.5 rounded-full border font-semibold transition-colors ${bookmarked ? 'bg-amber-50 border-amber-200 text-amber-600' : 'border-border hover:border-primary'}`}
            >
              <BookmarkSimple size={20} weight={bookmarked ? 'fill' : 'regular'} />
              {bookmarked ? 'Saved' : 'Save'}
            </button>
            <button 
              onClick={handleShare}
              data-testid="share-button"
              className="flex items-center gap-2 px-6 py-2.5 rounded-full border border-border font-semibold hover:border-primary transition-colors"
            >
              <ShareNetwork size={20} />
              Share
            </button>
            <div className="ml-auto">
              <AddToCollectionButton itemType="article" itemId={articleId} size="lg" />
            </div>
          </div>

          {/* Comments */}
          <section data-testid="comments-section">
            <h3 className="text-2xl mb-6" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 600 }}>
              <ChatCircle className="inline mr-2" size={24} />
              Comments ({comments.length})
            </h3>

            {user ? (
              <div className="mb-8">
                <textarea
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  placeholder="Share your thoughts..."
                  data-testid="comment-textarea"
                  className="w-full h-24 px-4 py-3 bg-card border border-border rounded-lg outline-none focus:border-primary resize-none"
                />
                <button
                  onClick={handleComment}
                  disabled={submitting || !commentText.trim()}
                  data-testid="submit-comment-button"
                  className="mt-3 bg-primary text-primary-foreground rounded-full py-2.5 px-6 font-semibold text-sm transition-transform hover:scale-[1.02] hover:opacity-90 disabled:opacity-50"
                >
                  {submitting ? 'Posting...' : 'Post Comment'}
                </button>
              </div>
            ) : (
              <div className="bg-card border border-border rounded-lg p-6 mb-8 text-center">
                <p className="text-muted-foreground mb-3">Sign in to leave a comment</p>
                <Link to="/login" data-testid="signin-to-comment-button" className="inline-block bg-primary text-primary-foreground rounded-full py-2.5 px-6 font-semibold text-sm">Sign In</Link>
              </div>
            )}

            <div className="space-y-6">
              {comments.map((comment) => (
                <div key={comment.comment_id} className="flex gap-4" data-testid={`comment-${comment.comment_id}`}>
                  <img 
                    src={comment.user_picture || `https://ui-avatars.com/api/?name=${comment.user_name}`}
                    alt={comment.user_name}
                    className="w-10 h-10 rounded-full flex-shrink-0"
                  />
                  <div className="flex-1">
                    <div className="bg-card border border-border rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <p className="font-semibold text-sm">{comment.user_name}</p>
                        <span className="text-xs text-muted-foreground">
                          {new Date(comment.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      <p className="text-base leading-relaxed">{comment.content}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>
      </article>
    </div>
  );
};

export default ArticleDetail;
